import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { css, Global } from '@emotion/core';
import styled from '@emotion/styled';
import { Row, Col } from 'emotion-flex';
import Image from 'gatsby-image';
import Container from './Container';
import { MonoFontLabel } from '../common/typography';
import Text from './Text';
import presets from '../../styles/presets';
import { ReactComponent as PhoneFrame } from '../../assets/images/phone-frame.svg';
import { ContentfulContentBlock } from '../../graphql-types';
import { SYSTEM_FONTS } from '../../styles/typography';

const Dot = styled.div<{ active?: boolean }>`
  width: 8px;
  height: 8px;
  background-color: #2aab3c;
  border-radius: 50%;
  opacity: ${({ active }) => (active ? 1 : 0.2)};
  margin-bottom: 25px;
  transition: opacity 0.5s ease-in;
  cursor: pointer;
`;

export default function Tracking({ items }: ContentfulContentBlock) {
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef(null);
  const itemsWithImages = items.filter((item) => item.image);
  const DURATION = 4000;

  const getColor = useCallback((index: number) => {
    switch (index) {
      case 0:
        return '#FFDAD5';
      case 1:
        return '#608BE4';
      case 2:
        return '#9775C1';
      default:
        return '#69B0AE';
    }
  }, []);

  const transitionToNextSlide = useCallback(() => {
    if (itemsWithImages.length > 1) {
      setActiveIndex((i) => (i + 1) % itemsWithImages.length);
    }
  }, [itemsWithImages]);

  const goToSlide = useCallback(
    (index: number) => {
      if (activeIndex !== index) {
        // reset existing interval if present
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        setActiveIndex(index);

        intervalRef.current = setInterval(transitionToNextSlide, DURATION);
      }
    },
    [transitionToNextSlide, activeIndex]
  );

  useEffect(() => {
    intervalRef.current = setInterval(transitionToNextSlide, DURATION);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [transitionToNextSlide]);

  return (
    <Container css={{ paddingTop: 48 }}>
      <Row css={{ alignItems: 'center' }}>
        <Col
          xs={12}
          lg={7}
          lgOffset={1}
          css={{
            marginBottom: 84,
            [presets.lg]: {
              marginBottom: 0,
            },
          }}
        >
          <div
            css={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: '500px',
              overflow: 'hidden',
              [presets.lg]: {
                minHeight: '1042px',
              },
            }}
          >
            <Background
              // fill={getColor(activeIndex)}
              css={{
                position: 'absolute',
                top: '80%',
                left: '85%',
                transform: 'translate(-50%, -50%)',
                width: '170%',
                height: 500,
                [presets.lg]: {
                  height: 2000,
                },
              }}
            />
            <PhoneFrame
              width={274}
              height={601}
              css={{
                position: 'absolute',
                width: 273,
                height: 601,
                left: '50%',
                marginLeft: -137, // width / 2
                top: '50%',
                marginTop: -294,
                zIndex: 2,
              }}
            />
            {itemsWithImages.map((item, i) => (
              <div
                key={item.id + i}
                css={[
                  {
                    opacity: 0,
                    position: 'absolute',
                    width: 274,
                    height: 588,
                    left: '50%',
                    marginLeft: -137, // width / 2
                    top: '50%',
                    marginTop: -287, // height / 2
                    zIndex: 1,
                    transition: 'opacity 1.5s ease-in',
                  },
                  i === activeIndex && {
                    opacity: 1,
                  },
                ]}
              >
                <Image
                  fluid={item.image.fluid}
                  css={{
                    width: 258,
                    marginLeft: 7,
                    marginTop: 16,
                    borderRadius: 20,
                  }}
                />
              </div>
            ))}
          </div>
          <div
            css={{
              display: 'none',
              visibility: 'hidden',
              [presets.lg]: {
                display: 'block',
                visibility: 'visible',
                position: 'absolute',
                top: '50%',
                right: 20,
                transform: 'translateY(-50%)',
              },
            }}
          >
            {itemsWithImages.map((item, i) => (
              <Dot
                key={`dot-${item.id}`}
                active={i === activeIndex}
                onClick={() => goToSlide(i)}
              />
            ))}
          </div>
        </Col>

        <Col xs={12} lg={4} lgOrder="first">
          <Global
            styles={css`
              .slide-in-fade-out-enter {
                opacity: 0;
                ${presets.lg} {
                  transform: translateY(64px);
                }
              }
              .slide-in-fade-out-exit {
                opacity: 1;
                transform: translateY(0);
              }
              .slide-in-fade-out-enter-active {
                opacity: 1;
                transform: translateY(0);
              }
              .slide-in-fade-out-exit-active {
                opacity: 0;
                transform: translateY(0);
              }
              .slide-in-fade-out-enter-active {
                transition: opacity 750ms ease-in-out, transform 750ms ease;
              }

              .slide-in-fade-out-exit-active {
                transition: opacity 400ms ease-out;
              }

              path#carousel-bg {
                transition: fill 750ms ease-out 400ms;
              }
            `}
          />
          <SwitchTransition mode="out-in">
            <CSSTransition<undefined>
              key={activeIndex}
              classNames="slide-in-fade-out"
              unmountOnExit
              addEndListener={(node, done) => {
                node.addEventListener('transitionend', done, false);
              }}
            >
              <div
                css={{
                  width: '100%',
                  textAlign: 'center',
                  maxWidth: 300,
                  margin: '0 auto',
                  [presets.md]: {
                    maxWidth: 'none',
                    margin: 0,
                    textAlign: 'left',
                  },
                }}
              >
                <MonoFontLabel>
                  {itemsWithImages[activeIndex].prefix}
                </MonoFontLabel>
                <h4
                  css={{
                    fontFamily: ['Blacker Pro Display']
                      .concat(SYSTEM_FONTS)
                      .join(', '),
                    fontSize: 32,
                    color: '#160B2C',
                  }}
                >
                  {itemsWithImages[activeIndex].heading}
                </h4>
                <Text>{itemsWithImages[activeIndex].shortText}</Text>
              </div>
            </CSSTransition>
          </SwitchTransition>
        </Col>
      </Row>
    </Container>
  );
}

function Background({
  fill,
  ...rest
}: React.PropsWithChildren<{ fill: string }>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 860 1027" {...rest}>
      <g transform="translate(-9 -9)" fill="none" fillRule="evenodd">
        <path
          id="carousel-bg"
          d="M222.636 631.768c-59.638-167.166 42.42-395.582 216.17-452.207 30.429-10.247 100.16-19.202 132.303.928 47.596 29.809 18.167 128.477 60.624 237.13 46.474 118.654 178.422 133.091 162.594 245.627-11.663 82.92-72.157 119.055-128.963 138.188-171.861 58.075-385.894-10.378-442.728-169.666z"
          fill={fill}
          fillRule="nonzero"
        />
        <g>
          <path
            d="M250.895 288.475C189.154 191.283 94.1827 194.12 82.9203 195.409C82.8421 195.416 82.8086 195.419 82.7415 195.426C82.3951 195.458 82.1179 195.506 81.9168 195.524C81.8832 195.527 81.8721 195.528 81.8385 195.532C81.6262 195.551 81.4942 195.586 81.4942 195.586C81.4942 195.586 81.4355 195.682 81.326 195.839C81.326 195.839 81.326 195.839 81.3159 195.851C81.2166 195.996 81.0899 196.21 80.9126 196.487C80.8923 196.511 80.8822 196.523 80.8629 196.559C75.3703 205.411 38.4879 285.17 100.691 383.087C153.769 466.638 265.849 495.417 265.849 495.417C265.849 495.417 303.971 372.004 250.882 288.454L250.895 288.475Z"
            fill="#C6D13E"
          />
        </g>
        <g>
          <path
            d="M294.199 355.948C348.711 287.905 420.075 297.864 428.459 299.762C428.518 299.774 428.543 299.779 428.593 299.789C428.851 299.842 429.056 299.901 429.206 299.932C429.232 299.937 429.24 299.939 429.265 299.944C429.423 299.976 429.52 300.013 429.52 300.013C429.52 300.013 429.556 300.09 429.626 300.217C429.626 300.217 429.626 300.217 429.633 300.227C429.696 300.344 429.774 300.516 429.885 300.738C429.898 300.759 429.905 300.769 429.916 300.797C433.331 307.91 454.599 370.963 399.68 439.513C352.818 498.007 265.965 510.425 265.965 510.425C265.965 510.425 247.341 414.424 294.211 355.933L294.199 355.948Z"
            fill="#235544"
          />
        </g>
        <path
          d="M29.6382 297.094C28.1201 295.941 28.1467 293.397 28.1467 293.397C28.1467 293.397 30.6341 292.722 32.1528 293.876C33.6716 295.029 33.6443 297.573 33.6443 297.573C33.6443 297.573 31.1556 298.247 29.6382 297.094Z"
          fill="#C6D13E"
        />
        <path
          d="M32.3478 292.103C32.3478 292.103 28.3599 294.623 23.6337 294.035"
          stroke="black"
          strokeWidth="0.591537"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.31224 290.564C6.70857 280.966 19.6532 277.423 25.1492 283.379C29.8364 288.459 24.3419 293.644 24.3419 293.644C24.3419 293.644 28.1634 300.467 22.3407 303.948C15.6373 307.955 3.83829 300.696 5.31224 290.564Z"
          fill="#1FA956"
        />
        <path
          d="M288.397 36.1703C279.806 38.8242 271.392 29.5621 271.948 19.7544C272.503 9.94664 277.207 3.70874 287.86 5.24701C298.512 6.78528 296.989 33.5163 288.397 36.1703Z"
          fill="#F9D26F"
        />
        <path
          d="M364.727 46.273C360.968 36.886 367.664 30.4248 373.422 26.8256C377.494 24.2804 382.667 25.2234 385.871 28.7955C390.137 33.5491 394.916 40.593 392.399 46.3033C388.182 55.8744 370.866 61.605 364.727 46.273Z"
          fill="#EC7EB0"
        />
        <path
          d="M380.835 40.8815C380.788 41.2869 380.693 41.6474 380.559 41.9679C378.792 46.2035 370.536 40.2293 374.162 37.4139V37.4139C376.426 35.6556 381.35 36.4524 380.835 40.8815Z"
          fill="#FFFDFC"
        />
        <path
          d="M380.759 55.5298C382.154 51.0933 385.239 44.1526 390.332 43.9177C392.354 43.8243 394.546 45.6308 396.63 48.217"
          stroke="#FFFDFC"
          strokeWidth="0.739421"
        />
        <path
          d="M392.675 38.624C389.659 38.3215 385.652 36.6043 385.696 33.895C385.716 32.6388 385.877 31.9411 387.579 30.1276"
          stroke="#FFFDFC"
          strokeWidth="0.739421"
        />
        <path
          d="M365.426 47.9157C369.663 45.9815 373.347 44.9568 376.93 48.5762C378.352 50.0133 378.687 52.5095 378.156 55.786"
          stroke="#FFFDFC"
          strokeWidth="0.739421"
        />
        <path
          d="M382.429 25.8623C379.811 31.8047 368.795 38.7653 364.025 35.7948"
          stroke="#FFFDFC"
          strokeWidth="0.739421"
        />
        <path
          d="M103.313 117.229C103.061 116.793 97.9841 117.853 97.194 117.577C93.9343 116.441 88.1014 108.302 87.3223 99.1943C86.9819 95.2153 87.5644 88.8056 86.947 86.7775C85.6435 82.4953 84.971 83.1895 83.1263 84.688C81.6038 85.9247 80.1844 88.0205 79.6354 89.427C77.7027 94.3774 78.1424 103.707 81.24 109.8C84.3375 115.892 91.889 121.592 97.1743 120.338C100.614 119.522 102.708 119.652 103.454 119.223C103.869 118.983 104.205 118.77 103.313 117.229Z"
          fill="#F9D26F"
        />
        <path
          d="M103.796 117.271C103.804 116.824 99.281 115.312 98.7867 114.742C96.747 112.39 95.1371 104.378 98.8545 97.0999C100.478 93.9202 102.381 91.7442 102.857 89.9208C103.863 86.071 103.85 85.413 101.677 85.7098C99.8836 85.9549 97.8139 87.059 96.4692 88.4486C92.8311 92.2083 90.3432 95.0364 89.8958 101.081C89.4484 107.126 92.6773 115.371 97.4573 116.831C100.568 117.782 102.164 118.836 102.959 118.85C103.401 118.858 103.769 118.849 103.796 117.271Z"
          fill="#F9D26F"
        />
        <path
          d="M43.9271 205.637C37.4084 209.012 26.2076 204.63 22.8241 197.793C22.218 196.597 21.8795 195.288 21.8324 193.957C21.8123 193.229 21.8847 192.503 22.0479 191.796C22.7219 188.853 26.5659 188.26 30.7805 185.915C35.3824 183.349 36.4668 178.495 40.7719 179.421C41.5889 179.595 42.3819 179.872 43.1316 180.246C45.321 181.337 46.9816 183.188 48.1133 185.42C51.5449 192.172 50.1387 202.421 43.9271 205.637Z"
          fill="#F098C0"
        />
        <path
          d="M37.1292 196.477C34.4443 197.72 30.4737 196.049 29.6224 193.483C29.4681 193.034 29.4223 192.543 29.4885 192.046C29.5268 191.773 29.6001 191.502 29.7066 191.238C30.148 190.14 31.643 189.929 33.3892 189.064C35.2961 188.118 36.0137 186.306 37.5877 186.665C37.8865 186.732 38.1697 186.838 38.4304 186.98C39.1917 187.394 39.7045 188.091 39.9927 188.929C40.8677 191.464 39.6875 195.292 37.1292 196.477Z"
          fill="#FFFDFC"
        />
        <path
          d="M182.315 54.0011C186.308 51.4161 188.43 47.349 192.426 41.8347C196.421 36.3204 199.081 34.225 196.472 30.2207C193.249 25.275 186.855 28.2541 184.001 28.9033C175.923 30.7405 171.358 32.0232 168.278 34.5484C165.197 37.0736 161.803 42.5966 167.04 50.0234C172.109 57.211 178.322 56.5861 182.315 54.0011Z"
          fill="#1FA956"
        />
        <ellipse
          cx="5.00133"
          cy="6.43479"
          rx="5.00133"
          ry="6.43479"
          transform="matrix(0.574365 0.818599 -0.819704 0.572788 179.896 35.1877)"
          fill="#FFFDFC"
        />
        <ellipse
          cx="13.0553"
          cy="10.3587"
          rx="13.0553"
          ry="10.3587"
          transform="matrix(0.545424 0.838161 -0.839179 0.543855 47.8291 366.244)"
          fill="#F9D26F"
        />
        <g>
          <path
            d="M423.142 540.742C423.142 540.742 423.122 540.745 423.112 540.747L423.145 540.746C423.145 540.746 411.119 556.009 395.797 556.805C377.977 557.728 369.655 546.098 368.816 544.562C368.816 544.562 368.781 544.489 368.779 544.479C369.422 542.84 376.272 530.224 394.05 528.65C409.334 527.29 423.142 540.742 423.142 540.742Z"
            fill="#DDF2F8"
          />
        </g>
        <path
          d="M382.292 541.483L399.541 556.233"
          stroke="#C6D13E"
          strokeWidth="4.92947"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M394.696 532.07L409.005 543.891"
          stroke="#FFDB00"
          strokeWidth="4.92947"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M397.517 525.818L413.545 539.241"
          stroke="#C6D13E"
          strokeWidth="4.92947"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M396.758 543.626L404.412 549.813"
          stroke="#53C0DA"
          strokeWidth="4.92947"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M208.107 554.137C208.107 554.137 197.203 551.773 194.211 548.156C191.219 544.539 193.312 538.6 190.01 533.649C186.709 528.699 182.462 529.281 176.541 529.373C170.62 529.466 166.119 517.125 166.119 517.125"
          stroke="black"
          strokeWidth="2.46474"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M195.987 547.7C197.209 547.256 198.556 547.886 198.999 549.099C199.441 550.313 198.815 551.659 197.593 552.103C196.371 552.547 195.024 551.917 194.582 550.704C194.139 549.49 194.765 548.144 195.987 547.7Z"
          fill="#FFFDFC"
          stroke="#53C0DA"
          strokeWidth="3.28631"
        />
        <path
          d="M188.708 531.301C189.93 530.858 191.277 531.487 191.72 532.701C192.162 533.914 191.536 535.261 190.314 535.704C189.092 536.148 187.745 535.519 187.302 534.305C186.86 533.092 187.486 531.745 188.708 531.301Z"
          fill="#FFFDFC"
          stroke="#F9D26F"
          strokeWidth="3.28631"
        />
        <path
          d="M170.451 523.9C171.673 523.456 173.02 524.085 173.462 525.299C173.905 526.513 173.279 527.859 172.057 528.303C170.835 528.747 169.488 528.117 169.045 526.903C168.603 525.69 169.229 524.343 170.451 523.9Z"
          fill="#FFFDFC"
          stroke="#1FA956"
          strokeWidth="3.28631"
        />
        <path
          d="M85.165 470.821C83.5136 463.082 93.0425 455.387 101.601 461.467C109.347 466.971 110.42 458.955 119.079 462.32C127.523 465.601 124.125 478.817 120.679 482.821C119.151 484.597 115.946 485.794 115.115 486.001C107.611 487.869 106.315 477.636 100.278 478.831C93.8195 480.11 86.8163 478.561 85.165 470.821Z"
          fill="#F098C0"
        />
        <path
          d="M276.084 560.476C288.308 553.637 296.87 558.45 300.618 565.798C304.365 573.146 304.079 583.589 290.006 588.765C275.933 593.94 263.859 567.315 276.084 560.476Z"
          fill="#F9D26F"
        />
        <path
          d="M283.084 548.865C290.632 548.364 303.443 554.375 302.883 558.872C302.324 563.37 291.167 573.616 286.71 572.589C282.252 571.562 275.536 549.366 283.084 548.865Z"
          fill="#FFFDFC"
        />
        <path
          d="M284.273 556.892C286.131 554.674 296.95 558.051 297.021 560.924C297.092 563.797 289.8 568.706 287.391 568.146C284.982 567.585 282.415 559.109 284.273 556.892Z"
          fill="#F9D26F"
        />
        <path
          d="M469.028 480.161C479.362 476.18 491.268 483.19 491.452 495.298C491.637 507.406 486.402 515.471 473.161 514.469C459.921 513.467 458.694 484.143 469.028 480.161Z"
          fill="#F098C0"
        />
        <path
          d="M460.726 492.44C460.726 492.44 467.747 500.997 477.219 497.018C486.691 493.038 492.768 501.257 492.768 501.257"
          stroke="#FFFDFC"
          strokeWidth="6.57263"
          strokeLinecap="round"
        />
        <path
          d="M452.291 175.426C460.45 163.61 461.903 148.837 461.686 138.528C461.476 128.881 459.816 121.214 459.355 119.286L459.025 117.992L457.82 118.173C456.055 118.469 448.218 119.895 439.083 123.516C429.493 127.324 416.448 134.393 408.725 146.505C401.533 157.793 398.244 170.264 399.225 182.565C399.811 189.915 401.761 195.579 403.105 198.55C403.479 199.376 403.804 199.996 404.043 200.381L404.274 200.751L404.707 200.82C404.981 200.86 432.751 203.72 452.291 175.426ZM436.862 188.869L409.976 193.799L413.882 187.86L444.299 182.281C441.862 184.845 439.361 187.026 436.862 188.869ZM446.385 179.969L415.318 185.667L419.311 179.597L451.12 173.764C450.989 173.96 450.87 174.16 450.736 174.349C449.323 176.394 447.87 178.247 446.385 179.969ZM452.463 171.586L420.74 177.408L424.784 171.251L455.371 165.642C454.547 167.676 453.584 169.664 452.466 171.594L452.463 171.586ZM435.327 127.223L439.457 145.478L435.433 151.598L430.478 129.663C432.102 128.773 433.727 127.965 435.327 127.223ZM459.019 128.495C459.214 130.096 459.395 131.849 459.53 133.742L448.048 135.849L452.041 129.779L459.03 128.499L459.019 128.495ZM446.351 134.983L443.829 123.816C445.621 123.197 447.31 122.671 448.876 122.218L450.375 128.862L446.351 134.983ZM457.795 121.005C458.058 122.273 458.423 124.19 458.772 126.616L453.47 127.59L457.795 121.005ZM444.906 137.179L440.901 143.281L437.089 126.436C438.774 125.701 440.43 125.043 442.028 124.457L444.906 137.179ZM431.654 160.803L435.698 154.646L459.194 150.337C458.969 152.182 458.673 154.051 458.288 155.923L431.657 160.811L431.654 160.803ZM457.841 157.924C457.384 159.819 456.823 161.708 456.166 163.566L426.229 169.055L430.221 162.985L457.849 157.92L457.841 157.924ZM437.135 152.454L441.127 146.384L459.741 142.968C459.687 144.727 459.583 146.536 459.408 148.368L437.135 152.454ZM433.989 153.794L429.901 160.017L423.947 133.695C425.521 132.59 427.127 131.581 428.746 130.638L433.981 153.798L433.989 153.794ZM428.457 162.213L424.432 168.333L417.729 138.698C419.186 137.337 420.705 136.074 422.274 134.899L428.449 162.216L428.457 162.213ZM422.991 170.537L418.987 176.64L411.89 145.247C413.195 143.47 414.623 141.802 416.15 140.253L422.999 170.534L422.991 170.537ZM459.771 141.037L442.564 144.191L446.608 138.034L459.65 135.641C459.743 137.361 459.784 139.168 459.771 141.037ZM455.88 120.465L451.816 126.658L450.698 121.704C452.797 121.138 454.563 120.741 455.888 120.462L455.88 120.465ZM410.427 147.361L417.542 178.836L413.518 184.957L406.57 154.236C407.635 152.029 408.869 149.791 410.316 147.52C410.348 147.469 410.387 147.415 410.427 147.361ZM405.276 157.086L412.074 187.153L408.101 193.204L402.16 166.931C402.845 163.782 403.848 160.489 405.279 157.094L405.276 157.086ZM401.326 171.828L406.656 195.401L404.963 197.974C403.379 194.473 399.768 184.694 401.326 171.828ZM406.537 199.033L408.539 195.991L432.92 191.519C421.308 198.606 410.45 199.126 406.537 199.033Z"
          fill="#53C0DA"
        />
        <path
          d="M443.501 104.747C442.471 105.244 441.297 105.315 440.205 104.935C439.122 104.551 438.251 103.774 437.741 102.738C436.827 100.829 437.406 98.5656 439.132 97.3474L439.76 96.9746C441.909 95.9351 444.493 96.8403 445.525 98.9711C446.439 100.88 445.86 103.143 444.134 104.362L443.506 104.734L443.501 104.747Z"
          fill="#A7A8A9"
        />
        <path
          d="M436.307 81.2424C435.936 81.5043 435.533 81.7363 435.11 81.9439C433.149 82.8887 430.919 83.0045 428.87 82.2883C426.808 81.5664 425.155 80.0741 424.21 78.1132C422.479 74.516 423.589 70.2221 426.844 67.9156C427.214 67.6537 427.618 67.4217 428.041 67.2141C432.099 65.2608 436.993 66.9739 438.946 71.0319C440.677 74.629 439.568 78.9229 436.312 81.2294L436.307 81.2424Z"
          fill="#A7A8A9"
        />
      </g>
    </svg>
  );
}
